<script setup lang="ts">
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import type { Media } from "@gasparigit/omnia-sdk";
import { LoaderCircle, Pause, Play, Volume2, VolumeX } from "lucide-vue-next";
import { computed, onMounted, ref } from "vue";

const props = defineProps<{
  file: Media;
}>();

const audioRef = ref<HTMLAudioElement>();
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const volume = ref(1);
const isMuted = ref(false);
const isLoading = ref(true);
const audioUrl = ref<string>();

onMounted(() => {
  if (props.file) {
    audioUrl.value = props.file?.download_url;
  }
});

const progress = computed(() => (duration.value ? (currentTime.value / duration.value) * 100 : 0));
const formattedCurrentTime = computed(() => formatTime(currentTime.value));
const formattedDuration = computed(() => formatTime(duration.value));

const onLoadedMetadata = () => {
  if (audioRef.value) {
    duration.value = audioRef.value.duration;
    isLoading.value = false;
  }
};

const onTimeUpdate = () => {
  if (audioRef.value) {
    currentTime.value = audioRef.value.currentTime;
  }
};

const onEnded = () => {
  isPlaying.value = false;
  currentTime.value = 0;
};

const togglePlay = async () => {
  if (!audioRef.value) return;

  try {
    if (isPlaying.value) {
      audioRef.value.pause();
      isPlaying.value = false;
    } else {
      await audioRef.value.play();
      isPlaying.value = true;
    }
  } catch (error) {}
};

const onProgressChange = (value: number[]) => {
  if (!audioRef.value || !duration.value) return;

  const newTime = (value[0] / 100) * duration.value;
  audioRef.value.currentTime = newTime;
  currentTime.value = newTime;
};

// const onVolumeChange = (value: number[]) => {
//   if (!audioRef.value) return;

//   const newVolume = value[0] / 100;
//   volume.value = newVolume;
//   audioRef.value.volume = newVolume;

//   if (newVolume === 0) {
//     isMuted.value = true;
//   } else if (isMuted.value) {
//     isMuted.value = false;
//   }
// };

const toggleMute = () => {
  if (!audioRef.value) return;

  if (isMuted.value) {
    audioRef.value.volume = volume.value;
    isMuted.value = false;
  } else {
    audioRef.value.volume = 0;
    isMuted.value = true;
  }
};

const formatTime = (seconds: number): string => {
  if (!isFinite(seconds)) return "0:00";

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};
</script>

<template>
  <div :class="cn('w-full p-2 bg-card border rounded-full shadow-sm', $attrs.class)">
    <audio class="hidden" ref="audioRef" :src="audioUrl" @loadedmetadata="onLoadedMetadata" @timeupdate="onTimeUpdate" @ended="onEnded" preload="metadata"></audio>

    <div class="flex items-center gap-4">
      <Button class="shrink-0 rounded-full" variant="default" size="icon" :disabled="isLoading" @click="togglePlay">
        <LoaderCircle v-if="isLoading" class="size-4 animate-spin" />
        <Play v-else-if="!isPlaying" class="size-4" />
        <Pause v-else class="size-4" />
        <span class="sr-only">{{ isPlaying ? "Pausa" : "Riproduci" }}</span>
      </Button>

      <div class="flex text-themeGray text-base gap-1">
        <span>{{ formattedCurrentTime }}</span>
        <span>/</span>
        <span>{{ formattedDuration }}</span>
      </div>

      <Slider :model-value="[progress]" :max="100" :step="0.1" :disabled="isLoading || !duration" class="w-full" @update:model-value="onProgressChange" />

      <Button class="shrink-0 rounded-full" variant="ghost" size="icon" @click="toggleMute">
        <Volume2 v-if="!isMuted && volume > 0" class="size-4" />
        <VolumeX v-else class="size-4" />
        <span class="sr-only">{{ isMuted ? "Attiva" : "Disattiva" }} il suono</span>
      </Button>

      <!-- <Popover>
        <PopoverTrigger as-child>
        </PopoverTrigger>
        <PopoverContent class="w-auto">
          <Slider class="w-32" :model-value="[isMuted ? 0 : volume * 100]" :max="100" :step="1" @update:model-value="onVolumeChange" />
        </PopoverContent>
      </Popover> -->
    </div>
  </div>
</template>
